import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BookOpen, Box, FileText, Newspaper, FileBadge2, Folder, Download, ArrowLeft, RefreshCw, FileDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuthStore, useLanguageStore, getLocalized } from '../store';
import { useTranslation } from '../i18n/translations';
import { SEO } from '../components/SEO';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const B2BDownloadsPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);
  const { user, token, isAuthenticated } = useAuthStore();
  
  const [downloads, setDownloads] = useState([]);
  const [catalogue, setCatalogue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [docLanguage, setDocLanguage] = useState(language);

  const refreshLabels = { ru: 'Обновить', nl: 'Vernieuwen', ro: 'Actualizează', en: 'Refresh' };
  const allResourcesLabels = { ru: 'Все материалы', nl: 'Alle bronnen', ro: 'Toate resursele', en: 'All Resources' };
  const docLangLabels = { ru: 'Язык материалов:', nl: 'Taal van documenten:', ro: 'Limba documentelor:', en: 'Resource Language:' };

  const CATEGORY_META = {
    catalogue:    { label: t.b2bDownloads?.categoryCatalogue || 'Catalogues',       icon: BookOpen },
    '3d_model':   { label: t.b2bDownloads?.category3dModel || '3D Models',        icon: Box },
    tech_pack:    { label: t.b2bDownloads?.categoryTechPack || 'Technical Packs',  icon: FileText },
    brochure:     { label: t.b2bDownloads?.categoryBrochure || 'Brochures',       icon: Newspaper },
    specification:{ label: t.b2bDownloads?.categorySpecification || 'Specifications', icon: FileBadge2 },
    other:        { label: t.b2bDownloads?.categoryOther || 'Other',               icon: Folder },
  };

  useEffect(() => {
    setDocLanguage(language);
  }, [language]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`/${language}/login`);
      return;
    }
    if (!user?.is_b2b || !user?.is_approved) {
      navigate(`/${language}/b2b`);
    }
  }, [isAuthenticated, user, navigate, language]);

  // ИСПРАВЛЕНО: Функция теперь принимает текущий docLanguage и запрашивает изолированный Collection Book ?lang=...
  const fetchData = useCallback(async (targetLang = docLanguage) => {
    if (!token) return;
    setLoading(true);
    
    const errorLabels = {
      ru: 'Не удалось загрузить технические файлы',
      nl: 'Laden van handelsbronnen mislukt',
      ro: 'Eșec la încărcarea resрсelor comerciale',
      en: 'Failed to load trade resources'
    };

    try {
      const [resAssets, resCat] = await Promise.all([
        fetch(`${API}/api/b2b/assets`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/site-settings/catalogue?lang=${targetLang}`) // ИСПРАВЛЕНО: передаем выбранный язык
      ]);
      
      if (resAssets.ok) {
        const d = await resAssets.json();
        const arr = d.assets || (Array.isArray(d) ? d : []);
        setDownloads(arr);
      }
      if (resCat.ok) {
        const c = await resCat.json();
        setCatalogue(c.catalogue || null);
      }
    } catch (err) {
      toast.error(errorLabels[targetLang] || 'Failed to load trade resources');
    } finally {
      setLoading(false);
    }
  }, [token, docLanguage]);

  // ИСПРАВЛЕНО: Срабатывает автоматически каждый раз, когда клиент переключает вкладку языка документов
  useEffect(() => {
    fetchData(docLanguage);
  }, [fetchData, docLanguage]);

  if (!isAuthenticated || !user?.is_b2b || !user?.is_approved) return null;

  const filtered = downloads.filter((d) => {
    const matchesCategory = activeFilter === 'all' || d.category === activeFilter;
    const is3DModel = d.category === '3d_model';
    const matchesLanguage = is3DModel || (d.language ? d.language === docLanguage : true);
    return matchesCategory && matchesLanguage;
  });

  const formatSize = (bytes) => {
    if (!bytes) return '';
    const kb = bytes / 1024;
    if (kb < 1024) return ` · ${Math.round(kb)} KB`;
    return ` · ${(kb / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="min-h-screen bg-[#F4F2EE] text-[#0A0A0A] font-sans pb-24" data-testid="b2b-downloads-page">
      <SEO title="Download Center · FEDYK Trade" description="B2B Trade Library — catalogues, 3D models, and technical documents." path="b2b/downloads" />

      {/* Header Profile Section */}
      <div className="bg-white border-b border-[#ECECEC] pt-28 pb-12 mb-12">
        <div className="container-custom">
          <Link
            to={`/${language}/b2b/dashboard`}
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.15em] uppercase text-[#9E9E9E] hover:text-[#0A0A0A] transition-colors mb-8 font-medium"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
            {t.b2bApiKeys?.backToDashboard || 'Back to dashboard'}
          </Link>

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <p className="text-[10px] tracking-[0.25em] uppercase text-[#9E9E9E] mb-3">
                {t.b2bDownloads?.eyebrow || 'Trade Library · Approved partners only'}
              </p>
              <h1 className="text-4xl md:text-5xl font-extralight tracking-tight text-[#0A0A0A] mb-4">
                {t.b2bDownloads?.title || 'Download Center'}
              </h1>
              <p className="max-w-2xl text-sm font-light text-[#4A4A4A] leading-relaxed">
                {t.b2bDownloads?.subtitle || 'Browse every trade resource we publish — full catalogues, 3D models, technical packs and brochures.'}
              </p>
            </div>
            <Button variant="outline" onClick={() => fetchData(docLanguage)} disabled={loading} className="rounded-none border-[#ECECEC] hover:bg-[#FAFAFA] text-xs uppercase tracking-wider px-4 py-2.5 font-light text-[#0A0A0A]">
              <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.5} />
              {refreshLabels[language] || 'Refresh'}
            </Button>
          </div>
        </div>
      </div>

      <div className="container-custom">
        {/* Featured Catalogue Book */}
        {catalogue?.secure_url && activeFilter === 'all' && (
          <section className="mb-16" data-testid="downloads-collection-book">
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#9E9E9E] mb-4">
              {t.b2bDownloads?.featuredCatalogue || 'Featured Catalogue'}
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 bg-white border border-[#ECECEC] p-8 md:p-10 shadow-sm">
              <div className="lg:col-span-4">
                {catalogue.cover_image_url ? (
                  <img
                    src={catalogue.cover_image_url}
                    alt={getLocalized(catalogue.title, language) || 'Collection Book'}
                    className="w-full aspect-[3/4] object-cover border border-[#ECECEC]"
                    data-testid="downloads-cover-img"
                  />
                ) : (
                  <div className="w-full aspect-[3/4] bg-[#0A0A0A] text-white flex items-center justify-center">
                    <BookOpen className="w-12 h-12 opacity-40" strokeWidth={1} />
                  </div>
                )}
              </div>
              <div className="lg:col-span-8 flex flex-col justify-center">
                <h2 className="text-3xl font-extralight tracking-tight text-[#0A0A0A] mb-3">
                  {getLocalized(catalogue.title, language) || 'Collection Book'}
                </h2>
                {getLocalized(catalogue.subtitle, language) && (
                  <p className="text-[10px] tracking-[0.25em] uppercase text-[#9E9E9E] mb-4">
                    {getLocalized(catalogue.subtitle, language)}
                  </p>
                )}
                <p className="text-sm font-light text-[#4A4A4A] leading-relaxed mb-8 max-w-lg">
                  {getLocalized(catalogue.description, language) || 'Our complete annual portfolio — every collection, every system, every finish.'}
                </p>
                <a
                  href={catalogue.secure_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={catalogue.filename || 'Collection-Book.pdf'}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 h-11 bg-[#0A0A0A] text-white !text-white text-xs tracking-[0.25em] uppercase hover:bg-[#1A1A1A] transition-all rounded-none font-medium w-fit shadow-sm"
                  data-testid="downloads-cb-link"
                >
                  <FileDown className="w-4 h-4 text-white/80" strokeWidth={1.5} />
                  {t.b2bDownloads?.downloadCollectionBook || 'Download Collection Book'}
                </a>
              </div>
            </div>
          </section>
        )}

        {/* Премиальный селектор языковых версий документов */}
        <div className="mb-6 flex flex-wrap items-center gap-4 border-b border-[#ECECEC] pb-5">
          <span className="text-[10px] tracking-[0.2em] uppercase text-[#9E9E9E] font-medium">
            {docLangLabels[language] || 'Document Language:'}
          </span>
          <div className="flex items-center gap-1.5">
            {['en', 'ro', 'nl', 'ru'].map((lang) => (
              <button
                key={lang}
                type="button"
                onClick={() => setDocLanguage(lang)}
                className={`px-3 py-1.5 text-[10px] tracking-widest uppercase font-medium transition-all rounded-none border ${
                  docLanguage === lang
                    ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]'
                    : 'bg-white text-[#4A4A4A] border-[#ECECEC] hover:border-[#0A0A0A]'
                }`}
              >
                {lang}
              </button>
            ))}
          </div>
        </div>

        {/* Category Filters */}
        <div className="mb-12 flex flex-wrap items-center gap-2" data-testid="downloads-category-filter">
          <button
            type="button"
            onClick={() => setActiveFilter('all')}
            className={`px-4 py-1.5 text-[11px] tracking-[0.15em] uppercase border transition-all ${
              activeFilter === 'all'
                ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]'
                : 'bg-white text-[#4A4A4A] border-[#ECECEC] hover:border-[#0A0A0A]'
            }`}
          >
            {allResourcesLabels[language] || 'All Resources'}
          </button>
          {Object.entries(CATEGORY_META).map(([key, meta]) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveFilter(key)}
              className={`px-4 py-1.5 text-[11px] tracking-[0.15em] uppercase border transition-all ${
                activeFilter === key
                  ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]'
                  : 'bg-white text-[#4A4A4A] border-[#ECECEC] hover:border-[#0A0A0A]'
              }`}
            >
              {meta.label}
            </button>
          ))}
        </div>

        {/* Library Assets */}
        {loading ? (
          <p className="text-sm font-light text-[#4A4A4A]">{t?.common?.loading || 'Loading...'}</p>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-dashed border-[#ECECEC] p-16 text-center" data-testid="downloads-empty">
            <Folder className="w-10 h-10 mx-auto mb-4 text-[#9E9E9E]" strokeWidth={1} />
            <p className="text-sm font-light text-[#4A4A4A]">
              {t.b2bDownloads?.noAssets || 'No additional trade assets uploaded yet. Check back soon.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((a) => {
              const aTitle = getLocalized(a.title, language);
              const aDesc = getLocalized(a.description, language);
              const meta = CATEGORY_META[a.category || 'other'] || CATEGORY_META.other;
              const Icon = meta.icon;
              return (
                <a
                  key={a.id}
                  href={a.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={a.filename || aTitle}
                  className="group bg-white border border-[#ECECEC] p-6 hover:border-[#0A0A0A] transition-all duration-300 shadow-sm flex flex-col"
                  data-testid={`downloads-item-${a.id}`}
                >
                  {a.image_url ? (
                    <div className="aspect-[4/3] -mx-6 -mt-6 mb-5 overflow-hidden bg-[#FAFAFA] border-b border-[#ECECEC]">
                      <img
                        src={a.image_url}
                        alt={aTitle}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-102"
                      />
                    </div>
                  ) : (
                    <div className="aspect-[4/3] -mx-6 -mt-6 mb-5 bg-[#0A0A0A] text-white flex items-center justify-center">
                      <Icon className="w-10 h-10 opacity-30" strokeWidth={1} />
                    </div>
                  )}
                  <div className="flex items-start gap-3 mb-4">
                    <FileDown className="w-4 h-4 mt-0.5 text-[#9E9E9E] group-hover:text-[#0A0A0A] transition-colors shrink-0" strokeWidth={1.5} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#0A0A0A] text-base leading-tight group-hover:underline">{aTitle}</p>
                      {a.sub_tag && (
                        <p className="text-[9px] tracking-[0.2em] uppercase text-[#9E9E9E] mt-2 font-medium">
                          {a.sub_tag}
                        </p>
                      )}
                    </div>
                  </div>
                  {aDesc && (
                    <p className="text-xs font-light text-[#4A4A4A] leading-relaxed mb-5">
                      {aDesc}
                    </p>
                  )}
                  <div className="mt-auto pt-4 border-t border-[#ECECEC] flex items-center justify-between text-[11px] text-[#9E9E9E]">
                    <span className="truncate max-w-[60%] font-mono tracking-wide">{a.filename || ''}</span>
                    <span className="tabular-nums font-light">{formatSize(a.file_size)}</span>
                  </div>
                </a>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default B2BDownloadsPage;
